import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { Message } from '@arco-design/web-react';
import type { ApiEnvelope } from '@/types/api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT ?? 15000);

const ACCESS_TOKEN_KEY = 'up_card_admin_access_token';

export function setAccessToken(token: string | null) {
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export const httpClient = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => {
    const envelope = response.data as ApiEnvelope<unknown>;
    if (envelope && typeof envelope === 'object' && 'code' in envelope) {
      if (envelope.code !== '0' && envelope.code !== 'OK') {
        const msg = envelope.message || `请求失败 (${envelope.code})`;
        Message.error(msg);
        return Promise.reject(
          Object.assign(new Error(msg), { isBusinessError: true, envelope }),
        );
      }
    }
    return response;
  },
  (error: AxiosError<ApiEnvelope<unknown>>) => {
    if (error.response?.status === 401) {
      setAccessToken(null);
      Message.error('登录已失效，请重新登录');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    const envelope = error.response?.data;
    const msg = envelope?.message || error.message || '请求失败';
    Message.error(msg);
    return Promise.reject(error);
  },
);

export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const res = await httpClient.request<ApiEnvelope<T>>(config);
  return res.data.data;
}
